import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {CutoutRefineLoading, CutoutRefineModeEntry, CutoutRefineModeSnapshot} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';
import {CUTOUT_REFINE_DEFAULT_BRUSH_SIZE, CutoutRefineEntryPoint} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';
import {CutoutRefineTool} from '@PosterWhiteboard/classes/item-refinement.types';

/** Mirror of PosterCutoutRefine for the Refine cutout panel; the domain class owns the values. */
export interface CutoutRefineModeState extends CutoutRefineModeSnapshot {
  isActive: boolean;
  itemId: string | null;
  entryPoint: CutoutRefineEntryPoint;
  /** Set while a session's images download, so the control that opened it can show a loader. */
  loadingItemId: string | null;
  loadingEntryPoint: CutoutRefineEntryPoint | null;
}

const initialState: CutoutRefineModeState = {
  isActive: false,
  itemId: null,
  entryPoint: CutoutRefineEntryPoint.ERASER,
  loadingItemId: null,
  loadingEntryPoint: null,
  tool: CutoutRefineTool.ERASE,
  brushSize: CUTOUT_REFINE_DEFAULT_BRUSH_SIZE,
  showOriginal: false,
  canResetAllEdits: false,
  canUndo: false,
  canRedo: false,
  isApplying: false,
};

const cutoutRefineMode = createSlice({
  name: 'cutoutRefineMode',
  initialState,
  reducers: {
    enterCutoutRefineMode: (_state: CutoutRefineModeState, action: PayloadAction<CutoutRefineModeEntry>) => {
      return {...action.payload, isActive: true, loadingItemId: null, loadingEntryPoint: null};
    },
    setCutoutRefineLoading: (state: CutoutRefineModeState, action: PayloadAction<CutoutRefineLoading | null>) => {
      state.loadingItemId = action.payload?.itemId ?? null;
      state.loadingEntryPoint = action.payload?.entryPoint ?? null;
    },
    updateCutoutRefineMode: (state: CutoutRefineModeState, action: PayloadAction<CutoutRefineModeSnapshot>) => {
      return {...state, ...action.payload};
    },
    exitCutoutRefineMode: () => {
      return initialState;
    },
  },
});

export const {enterCutoutRefineMode, updateCutoutRefineMode, exitCutoutRefineMode, setCutoutRefineLoading} = cutoutRefineMode.actions;
export const cutoutRefineModeReducer = cutoutRefineMode.reducer;
